/**
 * « Traduire »: the selected text in another language, in the reader. A web page cannot open the translation of the
 * device itself, so the passage is translated by the server (POST /translate, a Mistral model), the same everywhere.
 */

import { cleanSelection } from "./explain.ts";

/** The languages offered, the device's first. */
export const TRANSLATION_LANGUAGES = ["fr", "en", "es", "de", "it", "pt", "nl", "ar", "zh", "ja", "ru", "uk", "pl", "tr"] as const;

/** The server refuses longer texts. */
const MAX_TEXT = 5000;

const base = (language: string | undefined) => language?.trim().toLowerCase().split(/[-_]/)[0] ?? "";

/**
 * The language to translate into: the device's first language that is not the book's, then the interface's, then
 * French or English (whichever the book is not in).
 */
export function targetLanguage(bookLanguage: string, deviceLanguages: readonly string[], interfaceLanguage: string): string {
  const source = base(bookLanguage);
  for (const language of [...deviceLanguages, interfaceLanguage]) {
    const code = base(language);
    if (code.length === 2 && code !== source) return code;
  }
  return source === "fr" ? "en" : "fr";
}

/**
 * Whether the passage is in a language the reader may not read: known, and neither the device's language nor the
 * interface's. « Traduire » is then in the selection bar; otherwise it waits in the box of « Explique-moi ».
 */
export function isForeign(source: string, deviceLanguage: string | undefined, interfaceLanguage: string): boolean {
  return source !== "" && source !== base(deviceLanguage) && source !== base(interfaceLanguage);
}

/** Languages written in each script, the first one when the book does not say which. Kana anywhere is Japanese. */
const KANA = /[\p{Script=Hiragana}\p{Script=Katakana}]/u;
const SCRIPT_LANGUAGES: Array<[RegExp, string[]]> = [
  [/\p{Script=Han}/gu, ["zh", "ja"]],
  [/\p{Script=Hangul}/gu, ["ko"]],
  [/\p{Script=Arabic}/gu, ["ar", "fa", "ur"]],
  [/\p{Script=Hebrew}/gu, ["he", "yi"]],
  [/\p{Script=Cyrillic}/gu, ["ru", "uk", "bg", "sr"]],
  [/\p{Script=Greek}/gu, ["el"]],
  [/\p{Script=Thai}/gu, ["th"]],
  [/\p{Script=Devanagari}/gu, ["hi"]],
];

/**
 * The language of the selected text: its script when it is not the Latin alphabet (a Koran in Arabic whose EPUB says
 * nothing), the book's language otherwise; "" when nobody knows (the model then finds it).
 */
export function sourceLanguage(text: string, bookLanguage: string | undefined): string {
  const book = base(bookLanguage);
  const letters = text.match(/\p{L}/gu)?.length ?? 0;
  if (KANA.test(text)) return "ja";
  for (const [script, languages] of SCRIPT_LANGUAGES) {
    // The script of most of the letters.
    if ((text.match(script)?.length ?? 0) * 2 >= letters && letters > 0) return languages.includes(book) ? book : languages[0]!;
  }
  return book.length === 2 ? book : "";
}

/** Translates `text` with the server of the Studio or of the reader; null when it does not answer. */
export async function translateOnServer(
  url: string,
  headers: Record<string, string>,
  text: string,
  source: string,
  target: string,
  signal: AbortSignal,
): Promise<string | null> {
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ text: cleanSelection(text).slice(0, MAX_TEXT), source, target }),
      signal,
    });
    if (!response.ok) return null;
    const { translation } = (await response.json()) as { translation?: unknown };
    return typeof translation === "string" && translation.trim() ? translation : null;
  } catch {
    return null;
  }
}
